"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { EASE } from "@/lib/motion";
import { Icon } from "@/components/ui/Icon";
import { useDismiss } from "@/components/ui/useDismiss";
import { siteLocales } from "@/lib/i18n/localized";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { useContent } from "./ContentContext";
import { Flag } from "./Flag";

/* The interface-language dropdown, in the shell's icon-button idiom: the
   button wears the flag of the language in use, and each choice in the list
   its own. It renders only when the operator has enabled a second language
   at Admin → Languages — an English-only site keeps its chrome unchanged.
   Closes on a click outside or Escape, like the bar's other menus. `up`
   opens the list above the button, for a seat at the bottom of the rail.
   `icon` shrinks the trigger to a globe for the collapsed rail, and the
   list opens to its right, fixed to the viewport so the 64px column's
   overflow cannot clip it. */
export function LanguageSelector({ up = false, icon = false }: { up?: boolean; icon?: boolean }) {
  const { locale, enabledLocales, setLocale, t } = useI18n();
  const content = useContent();
  const showsFlag = content.i18n.selectorDisplay !== "Name";
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ left: number; bottom: number } | null>(null);
  const root = useRef<HTMLSpanElement>(null);

  useDismiss(root, open, () => setOpen(false));

  const toggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (icon && !open) {
      const rect = event.currentTarget.getBoundingClientRect();
      setAnchor({ left: rect.right + 8, bottom: window.innerHeight - rect.bottom });
    }
    setOpen((o) => !o);
  };

  if (enabledLocales.length < 2) {
    return null;
  }

  const all = siteLocales(content);
  const options = enabledLocales
    .map((code) => all.find((candidate) => candidate.code === code))
    .filter((candidate): candidate is (typeof all)[number] => candidate !== undefined);
  const current = options.find((option) => option.code === locale);

  return (
    <span ref={root} className="relative block">
      <button
        onClick={toggle}
        aria-label={`${t("common.language")}: ${current?.nativeName ?? locale}`}
        aria-expanded={open}
        title={current?.nativeName}
        className={cn(
          "text-fg transition-colors",
          icon
            ? "grid h-[40px] w-[40px] place-items-center rounded-lg"
            : "flex h-[38px] max-w-[150px] items-center gap-[8px] rounded-lg px-[10px] text-[12px] font-bold",
          open ? "bg-elevation-4" : icon ? "hover:bg-elevation-3" : "bg-elevation-3 hover:bg-elevation-4",
        )}
      >
        {icon ? (
          <Icon name="globe" size={20} />
        ) : (
          <>
            {showsFlag && <Flag code={locale} size={20} />}
            <span className="truncate">{current?.nativeName ?? locale}</span>
          </>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            /* In the rail the list is centered on the button — anchored left
               it ran past the rail's edge and was clipped. The horizontal
               centering rides the motion transform, which owns `transform`
               and would overwrite a CSS translate class. In icon mode the
               list is fixed beside the globe instead, bottoms aligned. */
            initial={{ opacity: 0, y: !icon && up ? 6 : 0, x: icon ? -6 : up ? "-50%" : 0 }}
            animate={{ opacity: 1, y: 0, x: icon ? 0 : up ? "-50%" : 0 }}
            exit={{ opacity: 0, y: !icon && up ? 6 : 0, x: icon ? -6 : up ? "-50%" : 0 }}
            transition={{ duration: 0.2, ease: EASE }}
            style={icon && anchor ? { left: anchor.left, bottom: anchor.bottom } : undefined}
            className={cn(
              "modal-shadow z-50 w-[200px] overflow-hidden rounded-xl border border-border bg-elevation-2 p-[6px]",
              icon ? "fixed" : cn("absolute", up ? "bottom-[46px] left-1/2" : "right-0 top-[46px]"),
            )}
          >
            {options.map((option) => (
              <button
                key={option.code}
                onClick={() => {
                  setLocale(option.code);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-[10px] rounded-lg px-[10px] py-[8px] text-left text-[13px] transition-colors",
                  option.code === locale ? "bg-elevation-4 text-fg" : "text-fg-muted hover:bg-elevation-3 hover:text-fg",
                )}
              >
                {showsFlag && <Flag code={option.code} size={18} />}
                <span className="flex-1">{option.nativeName}</span>
                {option.code === locale && <Icon name="check" size={16} />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
}
